import { AxiosError } from 'axios';

const STATUS_CODE_MESSAGES = {
  401: 'La autenticación ha fallado. Las credenciales son inválidas o han expirado.',
  403: 'No tiene permiso para realizar esta acción.',
  404: 'No se encontró la información solicitada.',
  405: 'La acción que se intentó realizar no está permitida en el servidor.',
  500: 'Se presentó un error interno en el servidor. Si el problema persiste, comuníquese con soporte técnico.',
  503: 'El servicio no está disponible en este momento. Intente nuevamente más tarde.',
  504: 'El servidor tardó demasiado en responder. Intente nuevamente más tarde.',
};

// Verifica si el mensaje tiene contenido HTML
const isHtml = (message) => message.toLowerCase().includes('<html');

// Normaliza el mensaje eliminando espacios en blanco al inicio y fina, además, asegura que el mensaje termine con un punto
const normalizeMessage = (message) => {
  if (!message) return '';
  const trimmed = message.trim();
  return trimmed.endsWith('.') ? trimmed : `${trimmed}.`;
};

export const getErrorMessage = (error, defaultMessage = '¡Algo salió mal! Inténtelo nuevamente. Si el problema persiste, comuníquese con soporte.') => {
  const normalizedDefaultMessage = typeof defaultMessage === 'string' ? normalizeMessage(defaultMessage) : defaultMessage;

  if (!error) return normalizedDefaultMessage;

  // Si es un error de Axios (petición HTTP fallida)
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const data = error.response?.data;

    // Si la data de la respuesta es un objeto con un campo message
    if (typeof data?.message === 'string' && data.message.trim()) {
      return normalizeMessage(data.message);
    }

    // Si la data proviene de un error de serializer de DRF. Solo maneja casos simples y no todos los posibles formatos ya que se usara solo hasta que el backend normalice los errores.
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const messages = [];

      for (const key of Object.keys(data)) {
        const value = data[key];
        if (Array.isArray(value)) {
          messages.push(`${key}: ${value.join(', ')}`);
        }
      }

      if (messages.length) {
        return normalizeMessage(messages.join(' | '));
      }
    }

    // Si la data de la respuesta es un string y no es HTML
    if (typeof data === 'string' && data.trim() && !isHtml(data)) {
      return normalizeMessage(data);
    }

    // Si hay un código HTTP reconocido, se retorna el mensaje correspondiente
    if (status && status in STATUS_CODE_MESSAGES) {
      return STATUS_CODE_MESSAGES[status];
    }

    return normalizedDefaultMessage;
  }

  // Si es un error genérico de JavaScript
  if (error instanceof Error && error.message?.trim()) {
    return normalizeMessage(error.message);
  }

  // Si es un objeto plano con un campo message
  if (typeof error === 'object' && typeof error.message === 'string' && error.message.trim()) {
    return normalizeMessage(error.message);
  }

  // Si es un string directamente
  if (typeof error === 'string' && error.trim()) {
    return normalizeMessage(error);
  }

  return normalizedDefaultMessage;
};